import React, { useState, useRef, useEffect, useMemo } from "react";

/**
 * Searchable single-select dropdown.
 * Props:
 *   - options: Array<{ value: string|number, label: string }>
 *   - value: selected value
 *   - onChange: (value) => void
 *   - placeholder: input placeholder when nothing selected
 *   - disabled: boolean
 *   - required: boolean
 *   - id: element id
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  required = false,
  id,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const selected = options.find((o) => String(o.value) === String(value));
    return selected ? selected.label : "";
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const displayValue = open ? query : selectedLabel;

  const handleSelect = (option) => {
    onChange(option.value);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open && filtered[highlight]) {
      e.preventDefault();
      handleSelect(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <input
        id={id}
        type="text"
        className="searchable-select__input"
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => !disabled && setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
        required={required}
      />
      <span className="searchable-select__chevron">▾</span>

      {open && filtered.length > 0 && (
        <ul className="searchable-select__dropdown">
          {filtered.map((option, idx) => {
            const isSelected = String(option.value) === String(value);
            const isHighlighted = idx === highlight;
            return (
              <li
                key={option.value}
                className={
                  "searchable-select__option" +
                  (isSelected ? " is-selected" : "") +
                  (isHighlighted ? " is-highlighted" : "")
                }
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}

      {open && filtered.length === 0 && (
        <ul className="searchable-select__dropdown">
          <li className="searchable-select__empty">ვერ მოიძებნა</li>
        </ul>
      )}
    </div>
  );
}
